<section class="phone-settings" aria-labelledby="phone-settings-title">
  <header class="settings-heading">
    <div>
      <h1 id="phone-settings-title">Phone &amp; voicemail</h1>
      @if (settings()) {
        <p>{{ subtitle() }}</p>
      }
    </div>
    @if (settings()) {
      <span
        class="state-badge"
        [class.is-active]="active()"
        [class.is-error]="
          state() === 'failed' || state() === 'support-required'
        ">
        @if (active()) {
          <span class="status-dot" aria-hidden="true"></span>
        }
        {{ statusLabel() }}
      </span>
    }
  </header>

  @if (loadError()) {
    <div class="form-alert" role="alert">
      <p>We could not refresh your phone settings. Please try again.</p>
      <button
        type="button"
        class="button secondary compact-action"
        (click)="recheck()"
        [disabled]="refreshing()">
        Try again
      </button>
    </div>
  }

  @if (settings(); as settings) {
    @if (active()) {
      <section class="number-strip" aria-label="Your phone numbers">
        <div class="number-group">
          <span class="phone-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M18.5 6C15.46 6 13 8.46 13 11.5c0 1.33.47 2.55 1.26 3.5H9.74c.79-.95 1.26-2.17 1.26-3.5C11 8.46 8.54 6 5.5 6S0 8.46 0 11.5 2.46 17 5.5 17h13c3.04 0 5.5-2.46 5.5-5.5S21.54 6 18.5 6Zm-13 9C3.57 15 2 13.43 2 11.5S3.57 8 5.5 8 9 9.57 9 11.5 7.43 15 5.5 15Zm13 0c-1.93 0-3.5-1.57-3.5-3.5S16.57 8 18.5 8 22 9.57 22 11.5 20.43 15 18.5 15Z" />
            </svg>
          </span>
          <div>
            <p class="eyebrow">Your voicemail number</p>
            <div class="voicemail-number">
              <p class="phone-number">{{ settings.phoneNumber }}</p>
              <span class="copy-control">
                <button
                  type="button"
                  class="copy-button"
                  aria-label="Copy voicemail number"
                  (click)="copyNumber()">
                  @if (copied()) {
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path
                        d="m4.5 10.5 3.5 3.5 7.5-8"
                        stroke="currentColor"
                        stroke-width="1.75"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  } @else {
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <rect
                        x="7"
                        y="7"
                        width="10"
                        height="10"
                        rx="2"
                        stroke="currentColor"
                        stroke-width="1.5" />
                      <path
                        d="M13 4.5V4.25A1.25 1.25 0 0 0 11.75 3h-7.5A1.25 1.25 0 0 0 3 4.25v7.5A1.25 1.25 0 0 0 4.25 13h.25"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round" />
                    </svg>
                  }
                </button>
                <!-- Shown for the copy timeout; the sr-only status below announces it. -->
                @if (copied()) {
                  <icaredx-tooltip-shell
                    class="copied-tooltip"
                    aria-hidden="true"
                    position="top">
                    Copied
                  </icaredx-tooltip-shell>
                }
              </span>
            </div>
            @if (copyError()) {
              <p class="inline-error" role="alert">
                Could not copy the number. Select the number above to copy it.
              </p>
            }
            <p class="small-help">
              Share this number with patients or add it to your website for
              calls you want handled by iCareDx AI voicemail.
            </p>
          </div>
        </div>
        <div class="practice-number">
          <p class="eyebrow">Your existing practice phone</p>
          @if (settings.practicePhone) {
            <p class="phone-number">{{ settings.practicePhone }}</p>
            <p class="small-help">
              This is the phone number saved in your profile. It is not
              connected to iCareDx AI voicemail.
            </p>
          } @else {
            <p class="phone-number">Not provided</p>
            <p class="small-help">
              Optional. Add a practice phone number in Account settings or your
              website editor.
            </p>
          }
        </div>
        <div class="number-actions">
          <button
            type="button"
            class="button primary"
            (click)="leave()"
            [disabled]="busy()">
            View calls
          </button>
        </div>
        <div class="website-number">
          <div class="switch-row website-number-row">
            <input
              #websiteNumberSwitch
              id="voicemail-website-number"
              class="switch-input"
              type="checkbox"
              role="switch"
              [checked]="settings.useVoicemailNumberOnWebsite"
              [disabled]="websiteNumberLocked()"
              (change)="setWebsiteNumber(websiteNumberSwitch)"
              aria-describedby="website-number-help" />
            <label class="switch-label" for="voicemail-website-number">
              <span class="switch-track" aria-hidden="true"></span>
              <span class="switch-title"
                >Make my default number on the website</span
              >
            </label>
            <!-- Outside the label so clicking it does not flip the switch. -->
            <button
              type="button"
              class="info-button"
              aria-label="About your website number"
              aria-describedby="website-number-help"
              [icaredxTooltip]="websiteNumberTooltip"
              icaredxTooltipPosition="top">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle
                  cx="10"
                  cy="10"
                  r="8.25"
                  stroke="currentColor"
                  stroke-width="1.5" />
                <path
                  d="M10 9v5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round" />
                <circle cx="10" cy="6.25" r="1" fill="currentColor" />
              </svg>
            </button>
            <!-- The tooltip is only in the DOM while shown; this keeps the text for screen readers. -->
            <span id="website-number-help" class="sr-only">{{
              websiteNumberHelp()
            }}</span>
            <ng-template #websiteNumberTooltip>
              <icaredx-tooltip-shell
                tooltipClass="[&>div>div]:break-normal"
                position="top">
                {{ websiteNumberHelp() }}
              </icaredx-tooltip-shell>
            </ng-template>
          </div>
          @if (websiteNumberError(); as error) {
            <p class="inline-error" role="alert">{{ error }}</p>
          }
        </div>
        <span class="sr-only" role="status">{{
          copied() ? 'Voicemail number copied to clipboard.' : ''
        }}</span>
      </section>
    } @else {
      <section class="setup-strip" aria-label="Your dedicated voicemail number">
        @switch (state()) {
          @case ('provisioning') {
            <div class="state-title">
              <span class="spinner" aria-hidden="true"></span>
              <h2>Setting up your voicemail line</h2>
            </div>
            <p role="status">
              Your number is being assigned. You can leave this page and check
              back.
            </p>
            <p class="small-help">
              Your Practice phone stays on your landing page until activation is
              complete.
            </p>
          }
          @case ('checking') {
            <div class="state-title">
              <span class="spinner" aria-hidden="true"></span>
              <h2>Checking your activation</h2>
            </div>
            <p>
              We are confirming whether your line was activated. You do not need
              to enable voicemail again.
            </p>
            <p class="small-help">
              Your Practice phone stays on your landing page until activation is
              confirmed.
            </p>
            <button
              type="button"
              class="button secondary compact-action"
              (click)="recheck()"
              [disabled]="refreshing()">
              {{ refreshing() ? 'Checking…' : 'Check status' }}
            </button>
          }
          @case ('support-required') {
            <h2>We need to finish setting up your line</h2>
            <p>
              Your activation needs help from our support team. Your Practice
              phone is still shown on your landing page.
            </p>
            <button
              type="button"
              class="button secondary compact-action"
              (click)="openDialog('support')">
              View support details
            </button>
          }
          @case ('failed') {
            <h2>We could not activate your voicemail line</h2>
            <p>
              Your settings are saved. Your Practice phone is still shown on
              your landing page.
            </p>
            <p class="small-help">
              Try again to finish activation. We will check your previous
              request first.
            </p>
          }
          @default {
            <h2>Your dedicated voicemail number</h2>
            <p>
              iCareDx assigns a U.S. voicemail-only number after activation. It
              replaces the Practice phone on your landing page.
            </p>
          }
        }
      </section>
    }

    @if (settingsMutationError(); as error) {
      <div class="form-alert" role="alert">{{ error }}</div>
    }

    <form
      [formGroup]="form"
      (ngSubmit)="active() ? save() : requestActivation()"
      novalidate>
      <fieldset [disabled]="busy() || unresolved()">
        <div class="settings-row">
          <div class="section-label">
            <label for="voicemail-greeting">Voicemail greeting</label>
            <p id="greeting-help">
              Your greeting plays before the required notice. Callers without a
              booked appointment then hear the text-link prompt before the tone.
            </p>
          </div>
          <div class="section-control">
            <div
              class="greeting-field"
              [class.invalid]="
                form.controls.greeting.invalid &&
                (form.controls.greeting.touched || submitted())
              ">
              <textarea
                id="voicemail-greeting"
                formControlName="greeting"
                rows="3"
                (input)="onInput()"
                aria-describedby="greeting-help greeting-count greeting-error"
                [attr.aria-invalid]="
                  form.controls.greeting.invalid &&
                  (form.controls.greeting.touched || submitted())
                "
                placeholder="Write the greeting callers will hear."></textarea>
              <span
                id="greeting-count"
                class="character-count"
                [class.over-limit]="draftGreeting().length > 500"
                >{{ draftGreeting().length }} / 500</span
              >
            </div>
            <div id="greeting-error">
              @if (
                form.controls.greeting.invalid &&
                (form.controls.greeting.touched || submitted())
              ) {
                <p class="inline-error">
                  {{
                    form.controls.greeting.hasError('required')
                      ? 'Enter a voicemail greeting.'
                      : 'Keep your greeting to 500 characters or fewer.'
                  }}
                </p>
              }
            </div>
            <div class="preview-actions">
              <button
                type="button"
                class="button secondary"
                (click)="openDialog('preview')">
                Preview call audio
              </button>
              <button
                type="button"
                class="button secondary"
                (click)="openDialog('notice')">
                Read required notice
              </button>
            </div>
          </div>
        </div>

        <div class="settings-row scheduling-row">
          <div class="section-label">
            <h2>Automatic follow-up SMS</h2>
            @if (settings.smsAvailable) {
              <p>
                One booking-link text, sent the moment a caller presses 1.
                Callers who already have a booked appointment are not offered
                the text. Everyone can still leave a voicemail.
              </p>
              <p class="secondary-help">
                Sent from the shared iCareDx text number, separate from your
                voicemail-only line. Messages are fixed and replies are not
                monitored.
              </p>
            } @else {
              <p>
                {{
                  active()
                    ? 'Your voicemail service remains active.'
                    : 'Voicemail can be enabled without follow-up texts.'
                }}
              </p>
            }
          </div>
          <div class="section-control">
            @if (settings.smsAvailable || !active()) {
              <div
                class="booking-link"
                [class.is-missing]="!bookingLink().url"
                role="group"
                aria-labelledby="booking-link-title">
                <p class="input-label" id="booking-link-title">
                  Booking link sent by text
                </p>
                <p class="small-help">
                  The link comes from your scheduling setup, so there is nothing
                  to re-enter.
                </p>
                @if (bookingLink().url; as url) {
                  <p class="booking-url">{{ displayUrl(url) }}</p>
                  <p class="small-help booking-source">
                    {{ sourceLabel() }} ·
                    <button
                      type="button"
                      class="text-link"
                      (click)="openIntegrations()">
                      Change in Integrations
                    </button>
                  </p>
                } @else {
                  <div class="booking-notice" role="status">
                    <p>{{ missingNotice() }}</p>
                    <p class="notice-actions">
                      <button
                        type="button"
                        class="text-link"
                        (click)="openIntegrations()">
                        Open Integrations
                      </button>
                      <button
                        type="button"
                        class="text-link"
                        (click)="openProviderWebsite()">
                        Open Provider Website
                      </button>
                    </p>
                  </div>
                }
              </div>
              <div class="switch-row">
                <input
                  id="voicemail-override"
                  class="switch-input"
                  type="checkbox"
                  role="switch"
                  formControlName="overrideEnabled"
                  (change)="onOverrideToggle()"
                  aria-describedby="override-help" />
                <label class="switch-label" for="voicemail-override">
                  <span class="switch-track" aria-hidden="true"></span>
                  <span class="switch-text">
                    <span class="switch-title"
                      >Send a different link by text</span
                    >
                    <span id="override-help" class="small-help">
                      Overrides your scheduling setup for these texts only.
                    </span>
                  </span>
                </label>
              </div>
              @if (draftOverrideEnabled()) {
                <div class="override-field">
                  <label class="input-label" for="voicemail-override-url"
                    >Link for texts</label
                  >
                  <input
                    id="voicemail-override-url"
                    formControlName="overrideUrl"
                    type="url"
                    inputmode="url"
                    autocomplete="url"
                    (input)="onInput()"
                    placeholder="https://calendly.com/your-page"
                    aria-describedby="override-error"
                    [class.invalid]="!!overrideError()"
                    [attr.aria-invalid]="!!overrideError()" />
                  <div id="override-error">
                    @if (overrideError(); as error) {
                      <p class="inline-error">{{ error }}</p>
                    }
                  </div>
                </div>
              }
            }
            @if (!settings.smsAvailable) {
              <div
                class="sms-preview"
                role="status"
                aria-label="Follow-up SMS availability">
                <p class="eyebrow">Follow-up SMS</p>
                <p class="sms-title">Follow-up texts unavailable</p>
                <p class="sms-message">
                  No automatic text messages are being sent.
                </p>
                <p class="small-help">
                  You can still receive, listen to and review voicemails.
                  Callers are not offered a text link while follow-up texts are
                  unavailable.
                </p>
              </div>
            } @else if (destination(); as destination) {
              <div class="sms-preview" aria-label="Booking link SMS preview">
                <p class="eyebrow">Follow-up SMS</p>
                <p class="sms-title">
                  Booking link · sent when the caller presses 1
                </p>
                <p class="small-help">
                  Only after the caller requests a text by pressing 1.
                </p>
                <p class="sms-message">{{ bookingMessage() }}</p>
              </div>
              <a
                class="button secondary open-link"
                [href]="destination"
                target="_blank"
                rel="noopener noreferrer"
                >Open booking link
                <span class="sr-only">(opens in a new tab)</span
                ><svg aria-hidden="true" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M11.5 3.5h5v5m0-5-8 8M8 4H4.5A1.5 1.5 0 0 0 3 5.5v10A1.5 1.5 0 0 0 4.5 17h10a1.5 1.5 0 0 0 1.5-1.5V12"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" /></svg
              ></a>
            } @else {
              <div
                class="sms-preview"
                role="status"
                aria-label="Booking link SMS preview">
                <p class="eyebrow">Follow-up SMS</p>
                <p class="sms-title">Booking link unavailable</p>
                <p class="sms-message">
                  No external link or published iCareDx page.
                </p>
                <p class="small-help">
                  Voicemail works without one. Add a booking destination if you
                  want callers offered a text with a link to book.
                </p>
              </div>
            }
          </div>
        </div>
      </fieldset>

      <footer class="settings-footer">
        <p class="save-status" role="status">
          {{
            saved()
              ? 'Changes saved.'
              : dirty()
                ? 'You have unsaved changes.'
                : ''
          }}
        </p>
        <div class="footer-actions">
          <button
            type="button"
            class="button secondary"
            (click)="leave()"
            [disabled]="busy() && !unresolved()">
            {{ unresolved() ? 'Back to calls' : 'Cancel' }}
          </button>
          @if (!unresolved()) {
            <button
              type="submit"
              class="button primary"
              [disabled]="busy() || (active() && !dirty())">
              @if (busy()) {
                <span class="spinner small" aria-hidden="true"></span>
              }
              {{
                busy()
                  ? 'Saving…'
                  : active()
                    ? 'Save changes'
                    : state() === 'failed'
                      ? 'Retry activation'
                      : 'Enable voicemail'
              }}
            </button>
          }
        </div>
      </footer>
    </form>
  } @else if (refreshing() || !loadError()) {
    <div class="settings-loading" role="status">
      <span class="spinner" aria-hidden="true"></span>Loading phone settings…
    </div>
  }
</section>

<dialog
  #dialog
  class="settings-dialog"
  [class.preview-dialog]="dialogKind() === 'preview'"
  aria-labelledby="settings-dialog-title"
  (cancel)="onDialogCancel($event)">
  @switch (dialogKind()) {
    @case ('notice') {
      <h2 id="settings-dialog-title">Required notices</h2>
      <p class="small-help">
        System-controlled audio, played after your greeting.
      </p>
      <h3>Recording and transcription notice</h3>
      <p class="disclosure">{{ disclosure() }}</p>
      <p class="small-help">
        Played to every caller before the tone. This notice cannot be edited.
      </p>
      <h3>Callers without a booked appointment · Text-link prompt</h3>
      <p class="disclosure">{{ smsOffer() }}</p>
      <p class="disclosure">
        <strong>After 1 is pressed:</strong> {{ smsConfirmation() }}
      </p>
      <p class="small-help">
        Offered to callers without a booked appointment when a booking text is
        available. Pressing 1 sends the text immediately; the caller can still
        leave a voicemail. This prompt cannot be edited.
      </p>
      <div class="dialog-actions">
        <button type="button" class="button primary" (click)="closeDialog()">
          Close
        </button>
      </div>
    }
    @case ('preview') {
      <h2 id="settings-dialog-title">Call audio preview</h2>
      <p class="small-help preview-description">
        {{
          previewCall() === 'booked'
            ? 'Preview what a caller with a booked appointment hears: no text-link prompt.'
            : 'Preview what callers hear with your current greeting.'
        }}
      </p>
      <div class="preview-tabs" role="group" aria-label="Call preview type">
        <button
          type="button"
          class="button secondary"
          [attr.aria-pressed]="previewCall() === 'booked'"
          (click)="selectPreviewCall('booked')">
          Booked appointment
        </button>
        <button
          type="button"
          class="button secondary"
          [attr.aria-pressed]="previewCall() === 'standard'"
          (click)="selectPreviewCall('standard')">
          Standard call
        </button>
      </div>
      <div class="preview-call-sequence">
        <p class="small-help">Call sequence</p>
        <p class="sequence-line">{{ sequenceLabel() }}</p>
      </div>
      @if (includeSmsOffer()) {
        <section class="preview-prompt" aria-labelledby="preview-prompt-title">
          <h3 id="preview-prompt-title">Text-link prompt</h3>
          <p>{{ smsOffer() }}</p>
          <p>After 1 is pressed: {{ smsConfirmation() }}</p>
          <p class="small-help">
            Played to callers without a booked appointment when a booking text
            is available.
          </p>
        </section>
      }
      @if (previewCall() === 'standard' && !promptOffered()) {
        <p class="small-help preview-unavailable">
          {{
            smsAvailable()
              ? 'No booking link is set, so callers are not offered the text-link prompt.'
              : 'Follow-up texts are unavailable, so callers are not offered the text-link prompt.'
          }}
        </p>
      }
      @if (form.controls.greeting.invalid) {
        <p class="inline-error">
          Enter a greeting of 1 to 500 characters before playing the preview.
        </p>
      }
      @if (preview.state() === 'error') {
        <p class="inline-error" role="alert">{{ preview.error() }}</p>
      }
      <p class="sr-only" role="status">
        {{
          preview.state() === 'loading'
            ? 'Preparing call audio…'
            : preview.state() === 'playing'
              ? 'Playing call audio.'
              : ''
        }}
      </p>
      <div class="preview-player" aria-label="Call audio player">
        <button type="button" class="button primary" (click)="togglePreview()">
          {{
            preview.state() === 'loading' || preview.state() === 'playing'
              ? 'Stop preview'
              : preview.state() === 'error'
                ? 'Retry audio preview'
                : 'Play preview'
          }}
        </button>
        <div
          class="preview-track"
          role="progressbar"
          aria-label="Call audio preview progress"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="
            preview.progress() === null ? null : (preview.progress() ?? 0) * 100
          ">
          <span
            class="preview-progress"
            [class.is-indeterminate]="preview.progress() === null"
            [style.width.%]="
              preview.progress() === null ? 35 : (preview.progress() ?? 0) * 100
            "></span>
        </div>
        <span class="preview-time" aria-label="Elapsed preview time">{{
          preview.elapsedLabel()
        }}</span>
      </div>
      <div class="dialog-actions preview-footer">
        <button
          type="button"
          class="button secondary"
          (click)="switchDialog('notice')">
          Read required notices
        </button>
        <button type="button" class="button secondary" (click)="closeDialog()">
          Close
        </button>
      </div>
    }
    @case ('activate') {
      <h2 id="settings-dialog-title">Enable voicemail?</h2>
      <p>
        Your new voicemail number replaces the Practice phone displayed on your
        published landing page.
      </p>
      <dl class="activation-summary">
        <div>
          <dt>Provider</dt>
          <dd>{{ settings()?.providerName }}</dd>
        </div>
        <div>
          <dt>Voicemail line</dt>
          <dd>Assigned automatically</dd>
        </div>
        <div>
          <dt>Call handling</dt>
          <dd>Voicemail only</dd>
        </div>
        <div>
          <dt>Booking link</dt>
          <dd>{{ bookingSummary() }}</dd>
        </div>
      </dl>
      <p>
        Calls to the new number go directly to voicemail. Calls to your existing
        practice number are not forwarded. You can leave while setup completes.
      </p>
      <div class="dialog-actions">
        <button type="button" class="button secondary" (click)="closeDialog()">
          Cancel
        </button>
        <button
          type="button"
          class="button primary"
          (click)="confirmActivation()">
          Enable voicemail
        </button>
      </div>
    }
    @case ('discard') {
      <h2 id="settings-dialog-title">Discard your changes?</h2>
      <p>
        You have unsaved changes to your voicemail settings. Leave without
        saving?
      </p>
      <div class="dialog-actions">
        <button type="button" class="button secondary" (click)="closeDialog()">
          Keep editing
        </button>
        <button type="button" class="button primary" (click)="discard()">
          Discard changes
        </button>
      </div>
    }
    @case ('support') {
      <h2 id="settings-dialog-title">Help with voicemail activation</h2>
      <p>
        Contact your iCareDx support team and ask them to check your voicemail
        activation. Your settings are saved and your Practice phone remains on
        your landing page.
      </p>
      <p>You do not need to submit another activation request.</p>
      <div class="dialog-actions">
        <button type="button" class="button primary" (click)="closeDialog()">
          Close
        </button>
      </div>
    }
  }
</dialog>
